import { AlertTriangle, ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import type { WeekSegment } from "@/lib/calendar-grid";
import { BORDER, JOB_STATUS_TINT } from "./job-chip";

// Exposed as a single const so a one-line swap to --color-warning is trivial
// after design review. Per ADR-0019: a genuine crew double-booking is the
// sanctioned exception to "faded not flagged" — requires coordinator action.
const CONFLICT_TINT = "var(--color-danger)";

/**
 * A spanning bar for one week-row segment of a production-window job. Placed
 * via explicit CSS Grid inline style (gridColumnStart / gridColumnEnd span /
 * gridRowStart); the parent CalendarMonthGrid provides the per-week grid
 * context. Strictly presentational — no hooks, no data fetch.
 *
 * ADR-0020 does NOT apply: this is a calendar layout element, not a chart.
 */
export function JobSpanBar({
  segment,
  style,
  onOpenJob,
}: {
  segment: WeekSegment;
  style: React.CSSProperties;
  onOpenJob: (id: string) => void;
}) {
  const tint = JOB_STATUS_TINT[segment.status] ?? "muted";
  const conflictLabel = segment.hasConflict
    ? `Crew double-booked: ${segment.label}`
    : undefined;

  const mergedStyle: React.CSSProperties = {
    ...style,
    ...(segment.hasConflict ? { outline: `1px solid ${CONFLICT_TINT}` } : {}),
  };

  return (
    <button
      type="button"
      onClick={() => onOpenJob(segment.id)}
      title={segment.label}
      aria-label={conflictLabel ?? segment.label}
      style={mergedStyle}
      className={cn(
        "flex h-5 w-full items-center gap-0.5 overflow-hidden",
        "border-l-2 px-1",
        "rounded-[var(--radius-sm)]",
        "bg-[color:var(--color-elevated)]",
        "text-xs text-[color:var(--color-text-md)]",
        "focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-accent)]",
        "transition-colors hover:bg-[color:var(--color-surface)]",
        BORDER[tint],
        segment.canceled && "opacity-60",
        segment.continuesLeft && "rounded-l-none",
        segment.continuesRight && "rounded-r-none",
      )}
    >
      {segment.continuesLeft && (
        <ChevronLeft
          className="size-3 shrink-0 text-[color:var(--color-text-lo)]"
          aria-hidden
        />
      )}
      {segment.hasConflict && (
        <AlertTriangle
          className="size-3 shrink-0"
          style={{ color: CONFLICT_TINT }}
          aria-hidden
        />
      )}
      <span
        className={cn(
          "min-w-0 flex-1 truncate text-left",
          segment.canceled && "line-through text-[color:var(--color-text-lo)]",
        )}
      >
        {segment.label}
      </span>
      {segment.continuesRight && (
        <ChevronRight
          className="size-3 shrink-0 text-[color:var(--color-text-lo)]"
          aria-hidden
        />
      )}
    </button>
  );
}
